/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/**
 * Travel trip-entity detail: the generic EntityDetail fed by tripEntityConfig.
 * Travel-specific: the trip link field, "View Trip" / "Edit in trip wizard"
 * header actions, and related lists whose Add opens the wizard step.
 */

import { useMemo } from "react";
import PropTypes from "prop-types";
import { IconButton, Tooltip } from "@mui/material";
import { Edit } from "@mui/icons-material";
import MDButton from "components/MDButton";
import { EntityDetail } from "components/EntityFramework";
import { commonIconButtonStyles, commonIconStyles } from "assets/theme/components/iconButtonStyles";
import { getTripEntityConfig, resolveNumericTripId } from "./tripEntityConfig";
import { wizardEditPath } from "./TripEntityList";

const tripLabel = (data) => {
    const tripId = resolveNumericTripId(data);
    return (
        data?.destination ||
        data?.primary_country ||
        data?.trip_title ||
        (tripId ? `Destination #${tripId}` : "-")
    );
};

export function buildTripDetailConfig(entityKey) {
    const config = getTripEntityConfig(entityKey);
    if (!config) return null;
    return {
        title: config.title,
        detailApiPath: config.detailApiPath,
        primaryField: config.primaryField,
        route: config.route,
        supportsDelete: config.supportsDelete,
        detailFields: (config.detailFields || []).map((field) => {
            if (!field.linkToTrip) return field;
            return {
                ...field,
                linkTo: (data) => {
                    const tripId = resolveNumericTripId(data);
                    return tripId ? `/trips/detail/${tripId}` : null;
                },
                renderValue: (_value, data) => tripLabel(data),
            };
        }),
        relatedRecords: (config.relatedRecords || []).map((section) => ({
            ...section,
            getAddPath: (record) => {
                const tripId = resolveNumericTripId(record);
                return tripId && section.addStep ? `/trips/${tripId}/edit?step=${section.addStep}` : null;
            },
        })),
        headerActions: (record, { navigate }) => {
            const tripId = resolveNumericTripId(record);
            if (!tripId) return null;
            return (
                <>
                    <MDButton
                        variant="outlined"
                        color="white"
                        size="small"
                        onClick={() => navigate(`/trips/detail/${tripId}`)}
                    >
                        View Trip
                    </MDButton>
                    <Tooltip title="Edit in trip wizard" arrow>
                        <IconButton
                            onClick={() => navigate(wizardEditPath(record, config.wizardStep))}
                            sx={commonIconButtonStyles}
                        >
                            <Edit sx={commonIconStyles} />
                        </IconButton>
                    </Tooltip>
                </>
            );
        },
    };
}

const TripEntityDetail = ({ entityKey }) => {
    const config = useMemo(() => buildTripDetailConfig(entityKey), [entityKey]);
    return <EntityDetail config={config} />;
};

TripEntityDetail.propTypes = {
    entityKey: PropTypes.string.isRequired,
};

export default TripEntityDetail;
